import React from 'react'
import { useAppContext } from '../../../providers/context'
import { ControlledModal } from '../../../layouts/components/modal'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { FloatingLoader } from '../../../components/simple/loader'
import { Button } from '../../../components/simple/button'
import { IconCheckSquare, IconEmail, IconPhone, IconText } from '../../../icons/library'
import { MessageTags, TagsInfo } from '../../home/messageTags'
import { BarChart, BarChartData } from '../../../components/charts/barChart'
import { Divider } from '../../../components/simple/divider'
import { usePreview } from '../preview'
import { Popups } from '../../../providers/popups'
import { MessageData } from '../../home/message'
import { SunburstData, SunburstChart } from '../../../components/charts/sunburstChart'
import { MessageInfoTable } from './messageInfoTable'
import { isMobileSize } from '../../../utils'

export enum DeliveryStatus {
  unreachable = 'unreachable',
  failed = 'failed',
  delivered = 'delivered',
  queued = 'queued',
  undelivered = 'undelivered',
  answered = 'answered',
  answeringMachine = 'answering machine',
  unknown = 'unknown',
  whatsapp = 'whatsapp',
  text = 'text',
  voicemailUnavailable = 'voicemail unavailable',
}

enum FilterMessageType {
  email = 'emails',
  text = 'texts',
  phone = 'calls',
}

enum FilterUserType {
  students = 'students',
  guardians = 'guardians',
  teachers = 'teachers',
  staff = 'staff',
}

export type MessageInfoFilter = {
  deliveryStatus?: DeliveryStatus
  messageType?: FilterMessageType
  userType?: FilterUserType
}

export const useMessageInfo = () => {
  const { setState } = useAppContext()
  const showMessageInfo = React.useCallback(
    (messageID: number, initialFilter?: MessageInfoFilter) => {
      return new Promise<void>((resolve) => {
        setState((s) => ({ ...s, messageInfoRequest: { messageID, initialFilter, onClose: resolve } }))
      })
    },
    [setState]
  )
  return { showMessageInfo }
}

interface MessageInfoLauncherProps extends React.HTMLAttributes<HTMLDivElement> {
  messageID: number
  initialFilter?: MessageInfoFilter
  cursor?: string
  canLaunch: boolean
}

export const MessageInfoLauncher: React.FC<MessageInfoLauncherProps> = ({
  messageID,
  initialFilter,
  cursor = 'cursor-help',
  canLaunch: canLaunchExternal,
  ...props
}) => {
  const { showMessageInfo } = useMessageInfo()
  const canLaunch = canLaunchExternal && !isMobileSize()
  const [mouseDownPos, setMouseDownPos] = React.useState<{ x: number; y: number } | null>(null)
  const launcherRef = React.useRef<HTMLDivElement>(null)

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setMouseDownPos({ x: e.clientX, y: e.clientY })
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mouseDownPos) {
      const distance = Math.pow(e.clientX - mouseDownPos.x, 2) + Math.pow(e.clientY - mouseDownPos.y, 2)
      if (distance > 25) return
    }

    let target: HTMLElement | null = e.target as HTMLElement
    while (target && target !== launcherRef.current) {
      if (['a', 'button'].includes(target.tagName.toLowerCase())) return
      target = target.parentElement
    }

    showMessageInfo(messageID, initialFilter)
    e.stopPropagation()
  }

  return (
    <div
      {...props}
      className={`${canLaunch ? cursor : ''} ${props.className}`}
      onMouseDown={canLaunch ? handleMouseDown : undefined}
      onClick={canLaunch ? handleClick : undefined}
      ref={launcherRef}
    >
      {props.children}
    </div>
  )
}

export interface MessageInfoRequest {
  messageID: number
  initialFilter?: MessageInfoFilter
  onClose: () => void
}

export interface MessageInfo {
  messageID: number
  originID: number
  tags: TagsInfo
  type: MessageData['type']
  showPreview: boolean
  showRefreshFlag: boolean
  stillProcessing: boolean
  timeInfo: {
    sendTime?: string
    callDuration?: string
  }
  confirmReceiptInfo?: {
    averageReadTime: string
  }
  teacherOnly: boolean
  archived: boolean
  canArchive: boolean
  emailStats: {
    total: number
    originalType: boolean
    pie: SunburstData
  }
  textStats: {
    total: number
    originalType: boolean
    pie: SunburstData
  }
  phoneStats: {
    total: number
    originalType: boolean
    pie: SunburstData
  }
  recipientBarChart: BarChartData
}

export const MessageInfoOverlay: React.FC<{ messageInfoRequest: MessageInfoRequest }> = ({ messageInfoRequest }) => {
  const { setState } = useAppContext()
  const { showExistingMessagePreview } = usePreview()

  const close = () => {
    setState((s) => ({ ...s, messageInfoRequest: undefined }))
    messageInfoRequest.onClose()
  }

  const messageIdData = React.useMemo(
    () => ({ messageID: messageInfoRequest.messageID }),
    [messageInfoRequest.messageID]
  )
  const { data: messageInfo, reload, loading } = useRequest<MessageInfo>('/frontend_api/messageInfo', messageIdData)
  const [archiveLoading, setArchiveLoading] = React.useState(false)

  const [filters, setFilters] = React.useState<MessageInfoFilter>(messageInfoRequest.initialFilter || {})

  const onChartLabelClick = (labels: string[]) => {
    const deliveryStatus = Object.values(DeliveryStatus).find((status) => labels.includes(status))
    const messageType = Object.values(FilterMessageType).find((type) => labels.includes(type))
    const userType = Object.values(FilterUserType).find((type) => labels.includes(type))
    setFilters({ deliveryStatus, messageType, userType })
  }

  return (
    <ControlledModal
      open={true}
      onOpenChange={(o) => {
        if (!o) close()
      }}
      disabled={loading || archiveLoading}
      reload={{
        fn: reload,
        flag: messageInfo?.stillProcessing
          ? 'This message is still processing. Click here to refresh message info:'
          : messageInfo?.showRefreshFlag
            ? 'This message is still being sent out. Click here to refresh stats:'
            : undefined,
      }}
      title="Message Info"
      className="w-[90%] sm:!mt-8"
    >
      {messageInfo === undefined || loading ? (
        <FloatingLoader />
      ) : messageInfo.stillProcessing ? (
        <div className="my-8 self-center rounded bg-warning px-4 py-2 font-bold shadow">
          This message is still processing, please check back in a bit
        </div>
      ) : (
        <>
          <div className="flex justify-between gap-2">
            <div className="flex min-w-0 flex-col gap-1">
              <MessageTags tags={messageInfo.tags} />
              <div>
                <b>{messageInfo.type === 'phone' ? 'Call time' : 'Send Time'}:</b> {messageInfo.timeInfo.sendTime}
              </div>
              {messageInfo.timeInfo.callDuration && (
                <div>
                  <b>Duration:</b> {messageInfo.timeInfo.callDuration}
                </div>
              )}
              {messageInfo.confirmReceiptInfo && (
                <>
                  <div className="flex items-center gap-2">
                    <b>Confirm Receipt</b>
                    <IconCheckSquare />
                  </div>
                  <div>
                    <b>Average Read Time: </b>
                    {messageInfo.confirmReceiptInfo.averageReadTime}
                  </div>
                </>
              )}
              {messageInfo.teacherOnly && <div className="font-weight-bold">This is a teacher only message</div>}
            </div>
            <div className="flex flex-col items-end gap-1">
              {messageInfo.showPreview && (
                <Button small onClick={() => showExistingMessagePreview(messageInfo.messageID)}>
                  See Original Preview
                </Button>
              )}
              {messageInfo.archived && (
                <div className="bg-warning">
                  <b>Thread Hidden:</b> only admins can see this thread.
                </div>
              )}
              {messageInfo.canArchive && (
                <ArchiveToggleButton
                  archived={messageInfo.archived}
                  toggle={(a) => {
                    setArchiveLoading(true)
                    makeRequest('/archive', {
                      id: messageInfo.originID,
                      archive: a,
                    })
                      .then(reload)
                      .catch(showAxiosErrorAsToast)
                      .finally(() => setArchiveLoading(false))
                  }}
                />
              )}
            </div>
          </div>
          <Divider className="my-2" />
          <div className="flex max-h-[350px] justify-around gap-4">
            <SunburstChart
              legend={{ side: 'top' }}
              data={messageInfo.emailStats?.pie}
              title={`Email (${messageInfo.emailStats?.total})`}
              centerIcon={
                <IconEmail
                  size="2x"
                  className={messageInfo.emailStats?.originalType ? 'text-email' : 'text-gray-200'}
                />
              }
              className="max-w-[250px] flex-grow basis-1"
              onLabelClick={(ls) => onChartLabelClick([FilterMessageType.email, ...ls])}
            />
            <SunburstChart
              legend={{ side: 'top' }}
              data={messageInfo.textStats?.pie}
              title={`Text (${messageInfo.textStats?.total})`}
              centerIcon={
                <IconText size="2x" className={messageInfo.textStats?.originalType ? 'text-text' : 'text-gray-200'} />
              }
              className="max-w-[250px] flex-grow basis-1"
              onLabelClick={(ls) => onChartLabelClick([FilterMessageType.text, ...ls])}
            />
            <SunburstChart
              legend={{ side: 'top' }}
              data={messageInfo.phoneStats?.pie}
              title={`Phone (${messageInfo.phoneStats?.total})`}
              centerIcon={
                <IconPhone
                  size="2x"
                  className={messageInfo.phoneStats?.originalType ? 'text-phone' : 'text-gray-200'}
                />
              }
              className="max-w-[250px] flex-grow basis-1"
              onLabelClick={(ls) => onChartLabelClick([FilterMessageType.phone, ...ls])}
            />
            <BarChart
              legend={{ side: 'top' }}
              data={messageInfo.recipientBarChart}
              title="Recipient Distribution"
              className="max-w-[400px] flex-grow-[2] basis-1"
              onLabelClick={onChartLabelClick}
            />
          </div>
          <Divider className="my-2" />
          <MessageInfoTable filters={filters} setFilters={setFilters} messageInfo={messageInfo} />
        </>
      )}
    </ControlledModal>
  )
}

const ArchiveToggleButton: React.FC<{ archived: boolean; toggle: (archive: boolean) => void }> = ({
  archived,
  toggle,
}) => {
  return archived ? (
    <Button
      small
      logo
      onClick={async () =>
        (await Popups.simpleConfirm(<>Are you sure you want to unhide this thread?</>)) && toggle(false)
      }
    >
      Unhide Thread
    </Button>
  ) : (
    <Button
      small
      logo
      onClick={async () =>
        (await Popups.confirm({
          title: <b>Are you sure you want to hide this thread?</b>,
          message: (
            <>No one besides admins will be able to see it. Admins can see this thread by selecting 'Show Hidden'.</>
          ),
          yesText: 'Yes',
          noText: 'No',
        })) && toggle(true)
      }
    >
      Hide Thread
    </Button>
  )
}
